/*
Copyright 2025 TekNix Corporation

This software is proprietary and confidential to TekNix Corporation.
All rights reserved. No part of this software may be reproduced, distributed,
or transmitted in any form or by any means, including photocopying, recording,
or other electronic or mechanical methods, without the prior written permission
of TekNix Corporation, except in the case of brief quotations embodied in
critical reviews and certain other noncommercial uses permitted by copyright law.

For permission requests, write to TekNix Corporation at the address below:
TekNix Corporation
Legal Department

UNAUTHORIZED REPRODUCTION OR DISTRIBUTION IS STRICTLY PROHIBITED.
*/

import type { MBeaconEventContent, MBeaconInfoContent, MBeaconInfoEventContent } from '../../@types/beacon';
import type { LocationAssetType, MAssetContent, MLocationContent } from '../../@types/location';
import type { XHubChatEvent } from '../event/event';
import { M_ASSET, M_LOCATION, M_TIMESTAMP } from '../../@types/location';
import { TypedEventEmitter } from '../../emit/typed-event-emitter';
import { sortEventsByLatestContentTimestamp } from '../../utils';

/**
 * Beacon info and beacon event types as described in MSC3672
 * https://github.com/matrix-org/matrix-spec-proposals/pull/3672
 */

/**
 * Beacon info events are state events.
 * We have two requirements for these events:
 * 1. they can only be written by their owner
 * 2. a user can have an arbitrary number of beacon_info events
 *
 * 1. is achieved by setting the state_key to the owners mxid.
 * Event keys in room state are a combination of `type` + `state_key`.
 * To achieve an arbitrary number of only owner-writable state events
 * we introduce a variable suffix to the event type
 *
 * @example
 * ```
 * {
 *      "type": "m.beacon_info.@matthew:matrix.org.1",
 *      "state_key": "@matthew:matrix.org",
 *      "content": {
 *          "m.beacon_info": {
 *              "description": "The Matthew Tracker",
 *              "timeout": 86400000,
 *          },
 *          // more content as described below
 *      }
 * },
 * {
 *      "type": "m.beacon_info.@matthew:matrix.org.2",
 *      "state_key": "@matthew:matrix.org",
 *      "content": {
 *          "m.beacon_info": {
 *              "description": "Another different Matthew tracker",
 *              "timeout": 400000,
 *          },
 *          // more content as described below
 *      }
 * }
 * ```
 */

/**
 * This class represents a live location beacon in a room.
 * Beacons are created by sending a m.beacon_info state event to a room.
 * Once created, the beacon can be started and stopped by sending
 * m.beacon events to the room.
 */

export type BeaconLocationState = Omit<MLocationContent, 'uri'> & {
  uri?: string; // override from MLocationContent to allow optionals
  timestamp?: number;
};

export const parseBeaconContent = (content: MBeaconEventContent): BeaconLocationState => {
  const location = M_LOCATION.findIn<MLocationContent>(content);
  const timestamp = M_TIMESTAMP.findIn<number>(content) ?? undefined;

  return {
    description: location?.description,
    uri: location?.uri,
    timestamp,
  };
};

export type BeaconInfoState = MBeaconInfoContent & {
  assetType?: LocationAssetType;
  timestamp?: number;
};

/**
 * Flatten beacon info event content
 */
export const parseBeaconInfoContent = (content: MBeaconInfoEventContent): BeaconInfoState => {
  const { description, timeout, live } = content;
  const timestamp = M_TIMESTAMP.findIn<number>(content) ?? undefined;
  const asset = M_ASSET.findIn<MAssetContent>(content);

  return {
    description,
    timeout,
    live,
    assetType: asset?.type,
    timestamp,
  };
};

export enum BeaconEvent {
  New = 'Beacon.new',
  Update = 'Beacon.update',
  LivenessChange = 'Beacon.LivenessChange',
  Destroy = 'Beacon.Destroy',
  LocationUpdate = 'Beacon.LocationUpdate',
}

export type BeaconEventHandlerMap = {
  [BeaconEvent.Update]: (event: XHubChatEvent, beacon: Beacon) => void;
  [BeaconEvent.LivenessChange]: (isLive: boolean, beacon: Beacon) => void;
  [BeaconEvent.Destroy]: (beaconIdentifier: string) => void;
  [BeaconEvent.LocationUpdate]: (locationState: BeaconLocationState) => void;
  [BeaconEvent.Destroy]: (beaconIdentifier: string) => void;
};

export const isTimestampInDuration = (startTimestamp: number, durationMs: number, timestamp: number): boolean =>
  timestamp >= startTimestamp && startTimestamp + durationMs >= timestamp;

// beacon info events are uniquely identified by
// `<roomId>_<state_key>`
export type BeaconIdentifier = string;
export const getBeaconInfoIdentifier = (event: XHubChatEvent): BeaconIdentifier =>
  `${event.getRoomId()}_${event.getStateKey()}`;

export class Beacon extends TypedEventEmitter<Exclude<BeaconEvent, BeaconEvent.New>, BeaconEventHandlerMap> {
  public readonly roomId: string;
  // beaconInfo is assigned by setBeaconInfo in the constructor
  // ! to make tsc believe it is definitely assigned
  private _beaconInfo!: BeaconInfoState;
  private _isLive?: boolean;
  private livenessWatchTimeout?: ReturnType<typeof setTimeout>;
  private _latestLocationEvent?: XHubChatEvent;

  public constructor(private rootEvent: XHubChatEvent) {
    super();
    this.roomId = this.rootEvent.getRoomId()!;
    this.setBeaconInfo(this.rootEvent);
  }

  public get isLive(): boolean {
    return !!this._isLive;
  }

  public get identifier(): BeaconIdentifier {
    return getBeaconInfoIdentifier(this.rootEvent);
  }

  public get beaconInfoId(): string {
    return this.rootEvent.getId()!;
  }

  public get beaconInfoOwner(): string {
    return this.rootEvent.getStateKey()!;
  }

  public get beaconInfoEventType(): string {
    return this.rootEvent.getType();
  }

  public get beaconInfo(): BeaconInfoState {
    return this._beaconInfo;
  }

  public get latestLocationState(): BeaconLocationState | undefined {
    return this._latestLocationEvent && parseBeaconContent(this._latestLocationEvent.getContent());
  }

  public get latestLocationEvent(): XHubChatEvent | undefined {
    return this._latestLocationEvent;
  }

  public update(beaconInfoEvent: XHubChatEvent): void {
    if (getBeaconInfoIdentifier(beaconInfoEvent) !== this.identifier) {
      throw new Error('Invalid updating event');
    }
    // don't update beacon with an older event
    if (beaconInfoEvent.getTs() < this.rootEvent.getTs()) {
      return;
    }
    this.rootEvent = beaconInfoEvent;
    this.setBeaconInfo(this.rootEvent);

    this.emit(BeaconEvent.Update, beaconInfoEvent, this);
    this.clearLatestLocation();
  }

  public destroy(): void {
    if (this.livenessWatchTimeout) {
      clearTimeout(this.livenessWatchTimeout);
    }

    this._isLive = false;
    this.emit(BeaconEvent.Destroy, this.identifier);
  }

  /**
   * Monitor liveness of a beacon
   * Emits BeaconEvent.LivenessChange when beacon expires
   */
  public monitorLiveness(): void {
    if (this.livenessWatchTimeout) {
      clearTimeout(this.livenessWatchTimeout);
    }

    this.checkLiveness();
    if (!this.beaconInfo) return;
    if (this.isLive) {
      const expiryInMs = this.beaconInfo.timestamp! + this.beaconInfo.timeout - Date.now();
      if (expiryInMs > 1) {
        this.livenessWatchTimeout = setTimeout(() => {
          this.monitorLiveness();
        }, expiryInMs);
      }
    } else if (this.beaconInfo.timestamp! > Date.now()) {
      // beacon start timestamp is in the future
      // check liveness again then
      this.livenessWatchTimeout = setTimeout(() => {
        this.monitorLiveness();
      }, this.beaconInfo.timestamp! - Date.now());
    }
  }

  /**
   * Process Beacon locations
   * Emits BeaconEvent.LocationUpdate
   */
  public addLocations(beaconLocationEvents: XHubChatEvent[]): void {
    // discard locations for beacons that are not live
    if (!this.isLive) {
      return;
    }

    const validLocationEvents = beaconLocationEvents.filter((event) => {
      const content = event.getContent<MBeaconEventContent>();
      const parsed = parseBeaconContent(content);
      if (!parsed.uri || !parsed.timestamp) return false; // we won't be able to process these
      const { timestamp } = parsed;
      return (
        this._beaconInfo.timestamp
        // only include positions that were taken inside the beacon's live period
        && isTimestampInDuration(this._beaconInfo.timestamp, this._beaconInfo.timeout, timestamp)
        // ignore positions older than our current latest location
        && (!this.latestLocationState || timestamp > this.latestLocationState.timestamp!)
      );
    });
    const latestLocationEvent = validLocationEvents.sort(sortEventsByLatestContentTimestamp)?.[0];

    if (latestLocationEvent) {
      this._latestLocationEvent = latestLocationEvent;
      this.emit(BeaconEvent.LocationUpdate, this.latestLocationState!);
    }
  }

  private clearLatestLocation = (): void => {
    this._latestLocationEvent = undefined;
    this.emit(BeaconEvent.LocationUpdate, this.latestLocationState!);
  };

  private setBeaconInfo(event: XHubChatEvent): void {
    this._beaconInfo = parseBeaconInfoContent(event.getContent());
    this.checkLiveness();
  }

  private checkLiveness(): void {
    const prevLiveness = this.isLive;

    // element web sets a beacon's start timestamp to the senders local current time
    // when Alice's system clock deviates slightly from Bob's a beacon Alice intended to be live
    // may have a start timestamp in the future from Bob's POV
    // handle this by adding 6min of leniency to the start timestamp when it is in the future
    if (!this.beaconInfo) return;
    const startTimestamp
      = this.beaconInfo.timestamp! > Date.now()
        ? this.beaconInfo.timestamp! - 360000 /* 6min */
        : this.beaconInfo.timestamp;
    this._isLive
      = !!this._beaconInfo.live
        && !!startTimestamp
        && isTimestampInDuration(startTimestamp, this._beaconInfo.timeout, Date.now());

    if (prevLiveness !== this.isLive) {
      this.emit(BeaconEvent.LivenessChange, this.isLive, this);
    }
  }
}
